import { GlobalConfigType } from 'actions'
import {
  isEffectivelyLocked,
  isExplicitlyUnlocked,
  LockScope,
  lockScopeToKey,
  LockState,
} from 'components/FieldLock/types'
import { isCustomOrigin } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { getLoanFieldLocks, setLoanFieldLock, setLoanFieldLockBatch } from 'services'
import { canEditLoanApplicationForBorrower } from 'utils/loanProcessRestriction'

function removeChildKeys(locks: LockState, scope: LockScope): LockState {
  const next: LockState = {}
  for (const [k, v] of Object.entries(locks)) {
    if (!isChildOfScope(k, scope)) next[k] = v
  }
  return next
}

function isChildOfScope(key: string, scope: LockScope): boolean {
  switch (scope.type) {
    case 'application':
      return key !== 'application'
    case 'borrower':
      return (
        key.startsWith(`section:${scope.borrowerSeperator}:`) ||
        key.startsWith(`subsection:${scope.borrowerSeperator}:`) ||
        key.startsWith(`field:${scope.borrowerSeperator}:`)
      )
    case 'section':
      return (
        key.startsWith(`subsection:${scope.borrowerSeperator}:${scope.fieldOrigin}:`) ||
        key.startsWith(`field:${scope.borrowerSeperator}:${scope.fieldOrigin}:`)
      )
    case 'page':
      return key.startsWith(`page_section:${scope.pageId}:`)
    default:
      return false
  }
}

export interface ILockContextValue {
  locks: LockState
  canLock: boolean
  isLocked: (scope: LockScope) => boolean
  isDirectlyLocked: (scope: LockScope) => boolean
  isBaseDisabled: (scope: LockScope) => boolean
  resolveCanEdit: (scope: LockScope, baseCanEdit: boolean) => boolean
  toggleLock: (scope: LockScope) => Promise<void>
  setLock: (scope: LockScope, locked: boolean | null) => Promise<void>
  setLockBatch: (updates: Array<{ scope: LockScope; locked: boolean | null }>) => Promise<void>
  loading: boolean
}

const LockContext = createContext<ILockContextValue>({
  locks: {},
  canLock: false,
  isLocked: () => false,
  isDirectlyLocked: () => false,
  isBaseDisabled: () => false,
  resolveCanEdit: (_scope, baseCanEdit) => baseCanEdit,
  toggleLock: async () => {},
  setLock: async () => {},
  setLockBatch: async () => {},
  loading: false,
})

export const useLockContext = () => useContext(LockContext)

interface LockProviderProps {
  children: React.ReactNode
  loanNumber?: number
}

export const LockProvider: React.FC<LockProviderProps> = ({ children, loanNumber: loanNumberProp }) => {
  const loanNumberFromStore = useSelector((state: any) => state.loanDetail?.loanNumber)
  const loanNumber: number = loanNumberProp ?? loanNumberFromStore

  const { hasPermission } = usePermissions()
  const canLock = useMemo(() => !!hasPermission('Loan.Application.LockFields'), [hasPermission])

  const [locks, setLocks] = useState<LockState>({})
  const [loading, setLoading] = useState(false)

  const loanNumberRef = useRef(loanNumber)
  loanNumberRef.current = loanNumber

  const globalConfig = useSelector((state: any) => state.globalConfigReducer)
  const authProfile = useSelector((state: any) => state.auth?.profile)
  const loanStatus = useSelector((state: any) => state.loanDetail?.loanStatus)
  const borrowerState = useSelector((state: any) => state.borrower)
  const restructureApplicationRelease = useSelector((state: any) => state.loanDetail?.restructureApplicationRelease)

  const configLocks = useMemo<LockState>(() => {
    const loanFieldsProps = globalConfig?.[GlobalConfigType.LoanFields] || {}
    const pageProps = loanFieldsProps.properties || {}

    let accountType = authProfile?.temparary ? 'borrower' : authProfile?.accountType
    if (accountType === 'borrower') {
      const profileEmail = authProfile?.email
      Object.keys(borrowerState || {}).forEach((borrowerSep) => {
        if (borrowerState[borrowerSep]?.email === profileEmail) accountType = borrowerSep
      })
    }

    const result: LockState = {}
    const borrowerSeps = Object.keys(borrowerState || {})
    const lockedByConfig = (prop: any) =>
      !!prop &&
      ((!!accountType && prop.lockByAccountTypes?.[accountType] === true) ||
        (!!loanStatus && prop.lockByLoanStatus?.[loanStatus] === true))

    Object.keys(pageProps).forEach((fieldOrigin) => {
      if (lockedByConfig(pageProps[fieldOrigin])) {
        borrowerSeps.forEach((borrowerSep) => {
          result[lockScopeToKey({ type: 'section', borrowerSeperator: borrowerSep, fieldOrigin })] = 1
        })
      }

      const fieldMap = loanFieldsProps[fieldOrigin]
      if (fieldMap && typeof fieldMap === 'object') {
        Object.keys(fieldMap).forEach((fieldKey) => {
          if (!lockedByConfig(fieldMap[fieldKey])) return
          borrowerSeps.forEach((borrowerSep) => {
            result[lockScopeToKey({ type: 'field', borrowerSeperator: borrowerSep, fieldOrigin, fieldKey })] = 1
          })
        })
      }
    })

    return result
  }, [globalConfig, authProfile, loanStatus, borrowerState])

  const effectiveLocks = useMemo<LockState>(() => {
    return { ...configLocks, ...locks }
  }, [locks, configLocks])

  useEffect(() => {
    if (!loanNumber) return
    setLoading(true)
    getLoanFieldLocks(loanNumber)
      .then((data) => setLocks(data ?? {}))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [loanNumber])

  const isLocked = useCallback((scope: LockScope) => isEffectivelyLocked(effectiveLocks, scope), [effectiveLocks])

  const isDirectlyLocked = useCallback((scope: LockScope) => !!effectiveLocks[lockScopeToKey(scope)], [effectiveLocks])

  const resolveCanEdit = useCallback(
    (scope: LockScope, baseCanEdit: boolean) => {
      if (isExplicitlyUnlocked(effectiveLocks, scope)) return true
      if (isEffectivelyLocked(effectiveLocks, scope)) return false
      return baseCanEdit
    },
    [effectiveLocks],
  )

  const isBaseDisabled = useCallback(
    (scope: LockScope) => {
      const borrowerSeperator = (scope as { borrowerSeperator?: string }).borrowerSeperator
      const fieldOrigin = (scope as { fieldOrigin?: string }).fieldOrigin
      const ignoreSign = fieldOrigin === 'credit' || (!!fieldOrigin && isCustomOrigin(fieldOrigin))
      if (!canEditLoanApplicationForBorrower(borrowerSeperator, ignoreSign)) return true
      if (authProfile?.isBorrower) {
        if (borrowerSeperator && borrowerState?.[borrowerSeperator]?.email !== authProfile?.email) return true
      }
      return false
    },
    [authProfile, borrowerState, restructureApplicationRelease],
  )

  const setLock = useCallback(async (scope: LockScope, locked: boolean | null) => {
    if (!loanNumberRef.current) return
    const key = lockScopeToKey(scope)
    setLocks((prev) => {
      const cleared = removeChildKeys(prev, scope)
      if (locked === null) {
        const rest = { ...cleared }
        delete rest[key]
        return rest
      }
      return { ...cleared, [key]: locked ? 1 : 0 }
    })
    await setLoanFieldLock(loanNumberRef.current, scope, locked)
  }, [])

  const toggleLock = useCallback(
    async (scope: LockScope) => {
      const locked = isEffectivelyLocked(effectiveLocks, scope)
      await setLock(scope, !locked)
    },
    [effectiveLocks, setLock],
  )

  const setLockBatch = useCallback(async (updates: Array<{ scope: LockScope; locked: boolean | null }>) => {
    if (!loanNumberRef.current) return
    setLocks((prev) => {
      const next = { ...prev }
      for (const { scope, locked } of updates) {
        const key = lockScopeToKey(scope)
        if (locked === null) {
          delete next[key]
        } else {
          next[key] = locked ? 1 : 0
        }
      }
      return next
    })
    await setLoanFieldLockBatch(loanNumberRef.current, updates)
  }, [])

  const value = useMemo<ILockContextValue>(
    () => ({
      locks: effectiveLocks,
      canLock,
      isLocked,
      isDirectlyLocked,
      isBaseDisabled,
      resolveCanEdit,
      toggleLock,
      setLock,
      setLockBatch,
      loading,
    }),
    [
      effectiveLocks,
      canLock,
      isLocked,
      isDirectlyLocked,
      isBaseDisabled,
      resolveCanEdit,
      toggleLock,
      setLock,
      setLockBatch,
      loading,
    ],
  )

  return <LockContext.Provider value={value}>{children}</LockContext.Provider>
}
